import { useState, useRef, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ArrowDown, ArrowUp } from "iconsax-react";
import { EmptyState } from "@/shared/components";
import { useAuth } from "@/shared/hooks";
import { Prompt, PromptForm, Select } from "@/shared/ui";
import {
  ACCOUNTING_TIMESHEET_DETAIL_PAGE_ROUTE,
  ACCOUNTING_CONTRACTOR_PAYROLL_DETAIL_PAGE_ROUTE,
  BOARD_PAGE_ROUTE,
  PROFILE_PAGE_ROUTE,
  extractErrorMessage,
  downloadBlob,
  ensurePdfExtension,
} from "@/shared/utils";
import { LeaveDetailsModal, MedicalLeaveDetailsModal } from "@/features/hr/leave-applications";
import { AmendmentDetailsModal } from "@/features/hr/amendments";
import { ResignationLetterDetailsModal } from "@/features/hr/resignation-letters";
import OrderDetailsModal from "@/features/hr/contracts/pages/ContractsListPage/components/OrderDetailsModal";
import JobApplicationDetailsModal from "@/features/hr/contracts/pages/ContractsListPage/components/JobApplicationDetailsModal";
import {
  useDownloadWorkContractPreviewMutation,
  useSubmitContractForSigningMutation,
  usePreviewJobApplicationMutation,
} from "@/features/hr/contracts/api";
import type { LeaveType } from "@/features/hr/leave-applications";
import {
  useApproveItemRequestMutation,
  useRejectItemRequestMutation,
  useCancelItemRequestMutation,
  useFulfillItemRequestMutation,
} from "@/features/accounting/warehouse";
import { useOrderSigning } from "@/features/signing";
import type { SelfServiceRequest } from "@/features/self/requests/types";
import type { NormalizedPendingItem } from "../hooks/usePendingActions";
import { usePendingActionsPage } from "../hooks/usePendingActionsPage";
import type { SortOption } from "../hooks/usePendingActionsPage";
import PendingActionCard from "../components/PendingActionCard";
import PendingActionsPageSkeleton from "../components/PendingActionsPageSkeleton";
import PendingActionsFilterPanel from "../components/PendingActionsFilterPanel";
import AcknowledgeOrderModal from "../components/AcknowledgeOrderModal";
import CounterSignDocumentModal from "../components/CounterSignDocumentModal";
import { buildSelfServiceRequestFromPending } from "../utils";
import type { ActionType } from "../types/pending.types";
import {
  isSignDocumentDetails,
  isAwaitingSignatureDetails,
  isOrderDetails,
  isLeaveApprovalDetails,
  isTimesheetApprovalDetails,
  isGPHPaymentReviewDetails,
  isTaskDeadlineDetails,
  isFSMSubmissionDetails,
  isAdvanceApprovalDetails,
  isProjectRemovalRequestDetails,
} from "../types/pending.types";

const RequestDetailModal = lazy(() =>
  import("@/features/accounting/warehouse/components/RequestDetailModal").then((m) => ({
    default: m.default,
  })),
);
const FulfillRequestModal = lazy(() =>
  import("@/features/accounting/warehouse/components/FulfillRequestModal").then((m) => ({
    default: m.default,
  })),
);
const OrgSignDocumentModal = lazy(() =>
  import("../components/OrgSignDocumentModal").then((m) => ({
    default: m.default,
  })),
);
const CompletionActSignModal = lazy(() =>
  import("../components/CompletionActSignModal").then((m) => ({
    default: m.default,
  })),
);
const EditSelfLeaveForm = lazy(() =>
  import("@/features/self/requests/components/EditSelfLeaveForm").then((m) => ({
    default: m.default,
  })),
);
const EditSelfAmendmentForm = lazy(() =>
  import("@/features/self/requests/components/EditSelfAmendmentForm").then((m) => ({
    default: m.default,
  })),
);
const EditSelfResignationForm = lazy(() =>
  import("@/features/self/requests/components/EditSelfResignationForm").then((m) => ({
    default: m.default,
  })),
);
const FSMReviewModal = lazy(() =>
  import("../components/FSMReviewModal").then((m) => ({
    default: m.default,
  })),
);
const DataChangeApprovalModal = lazy(() =>
  import("../components/DataChangeApprovalModal").then((m) => ({
    default: m.default,
  })),
);
const ProjectRemovalApprovalModal = lazy(() =>
  import("../components/ProjectRemovalApprovalModal").then((m) => ({
    default: m.default,
  })),
);
const AdvanceDetailsModal = lazy(() =>
  import("@/features/accounting/advances").then((m) => ({
    default: m.AdvanceDetailsModal,
  })),
);
const SignPersonalSheetModal = lazy(() =>
  import("../components/SignPersonalSheetModal").then((m) => ({
    default: m.default,
  })),
);
const OrgSignByDocumentModal = lazy(() =>
  import("../components/OrgSignByDocumentModal").then((m) => ({
    default: m.default,
  })),
);

const LEGAL_SOURCE_TYPES = [
  "service_agreement_individual",
  "service_agreement_msb",
  "vehicle_rental",
  "premises_lease",
  "goods_supply",
  "vehicle_handover",
  "premises_handover",
];

export default function PendingActionsPage() {
  const { t: tCommon } = useTranslation("Common");
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [activeItem, setActiveItem] = useState<NormalizedPendingItem | null>(null);
  const [acknowledgeOrderData, setAcknowledgeOrderData] = useState<{
    orderId: number | null;
    orderType: ActionType;
    documentUrl: string | null;
    unsignedDocumentUrl: string | null;
    leaveType?: string;
  } | null>(null);
  const [itemRequestId, setItemRequestId] = useState<number | null>(null);
  const [fulfillRequestId, setFulfillRequestId] = useState<number | null>(null);

  const [counterSignData, setCounterSignData] = useState<{
    documentId: string;
    bytesUrl?: string;
    fileUrl?: string;
    workerName: string;
    documentType: string;
    signingStatus: 0 | 1 | 2;
  } | null>(null);
  const [jobAppSignData, setJobAppSignData] = useState<{
    candidateId: number;
    candidateName: string;
    applicationId: number;
  } | null>(null);
  const [signingOrderData, setSigningOrderData] = useState<{
    orderId: number;
    candidateName: string;
    orderUrl: string | null;
  } | null>(null);

  const [signingLeaveData, setSigningLeaveData] = useState<{ leaveId: number; leaveType: LeaveType } | null>(null);
  const [signingAmendmentId, setSigningAmendmentId] = useState<number | null>(null);
  const [signingResignationId, setSigningResignationId] = useState<number | null>(null);

  const [orgSignData, setOrgSignData] = useState<{
    sourceType: string;
    entityId: number;
    documentType: string;
  } | null>(null);

  const [reviseRequest, setReviseRequest] = useState<SelfServiceRequest | null>(null);
  const [waitItem, setWaitItem] = useState<NormalizedPendingItem | null>(null);
  const [fsmReviewId, setFsmReviewId] = useState<string | null>(null);
  const [dataChangeReviewData, setDataChangeReviewData] = useState<{
    requestId: number;
    actionRequired: "approve_data_change" | "approve_data_change_chief";
  } | null>(null);
  const [projectRemovalReview, setProjectRemovalReview] = useState<{
    projectId: number;
    requestId: number;
    projectName: string;
    actionType: "delete" | "archive";
    reason: string;
    initiatedBy: string;
  } | null>(null);
  const [advanceReview, setAdvanceReview] = useState<{ id: number; type: "advance" | "trip" } | null>(null);
  const [showSignPersonalSheet, setShowSignPersonalSheet] = useState(false);
  const [advanceOrgSignData, setAdvanceOrgSignData] = useState<{
    documentId: string;
    bytesUrl?: string;
    workerName: string;
    documentType: string;
    advanceId?: number;
  } | null>(null);

  const isSigningModalOpen =
    counterSignData !== null ||
    jobAppSignData !== null ||
    signingOrderData !== null ||
    signingLeaveData !== null ||
    signingAmendmentId !== null ||
    signingResignationId !== null ||
    orgSignData !== null ||
    showSignPersonalSheet ||
    advanceOrgSignData !== null ||
    advanceReview !== null ||
    (activeItem !== null && activeItem.type === "signing_act");
  const isFsmModalOpen = fsmReviewId !== null;

  const {
    t,
    items,
    filteredItems,
    isLoading,
    refetch,
    searchQuery,
    setSearchQuery,
    selectedCategories,
    handleCategoryToggle,
    selectedPriorities,
    handlePriorityToggle,
    dateRange,
    setDateRange,
    sortBy,
    setSortBy,
    handleResetFilters,
    hasActiveFilters,
  } = usePendingActionsPage({ pauseRefetch: isSigningModalOpen || isFsmModalOpen });

  const sortOptions: { label: string; value: SortOption; icon: React.ReactNode }[] = [
    { label: t("sort.priorityDesc"), value: "priority_desc", icon: <ArrowDown size={16} color="currentColor" /> },
    { label: t("sort.priorityAsc"), value: "priority_asc", icon: <ArrowUp size={16} color="currentColor" /> },
    { label: t("sort.dateDesc"), value: "date_desc", icon: <ArrowDown size={16} color="currentColor" /> },
    { label: t("sort.dateAsc"), value: "date_asc", icon: <ArrowUp size={16} color="currentColor" /> },
  ];

  const [approveItemRequest] = useApproveItemRequestMutation();
  const [rejectItemRequest] = useRejectItemRequestMutation();
  const [cancelItemRequest] = useCancelItemRequestMutation();
  const [fulfillItemRequest] = useFulfillItemRequestMutation();
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const [confirmReject, setConfirmReject] = useState<{ id: number; reason: string } | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<number | null>(null);

  const signingContractIdRef = useRef<number | null>(null);
  const [downloadContractPreview] = useDownloadWorkContractPreviewMutation();
  const [submitContractForSigning] = useSubmitContractForSigningMutation();
  const [previewJobApplication] = usePreviewJobApplicationMutation();
  const [isPreviewingJobApp, setIsPreviewingJobApp] = useState(false);

  const contractSigning = useOrderSigning({
    previewFn() {
      return downloadContractPreview(signingContractIdRef.current!)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature, fileBase64) {
      return submitContractForSigning({
        id: signingContractIdRef.current!,
        cms_signature: cmsSignature,
        file_base64: fileBase64!,
      }).unwrap();
    },
    onSuccess() {
      setPrompt({ title: t("messages.successTitle"), text: t("messages.contractSignSuccess"), variant: "success" });
      signingContractIdRef.current = null;
      refetch();
    },
    setPrompt,
    t: tCommon,
  });

  async function handleItemClick(item: NormalizedPendingItem) {
    if (item.actionRequired === "wait") {
      setWaitItem(item);
      return;
    }
    if (item.actionRequired === "fill_personal_sheet") {
      navigate(PROFILE_PAGE_ROUTE);
      return;
    }
    if (item.actionRequired === "sign_personal_sheet") {
      setShowSignPersonalSheet(true);
      return;
    }
    if (item.actionRequired === "complete_task") {
      if (isTaskDeadlineDetails(item.details)) {
        navigate(`${BOARD_PAGE_ROUTE}?project=${item.details.project_id}&taskId=${item.details.task_id}`);
      } else {
        navigate(BOARD_PAGE_ROUTE);
      }
      return;
    }
    if (item.actionRequired === "revise_application") {
      const selfRequest = buildSelfServiceRequestFromPending(item);
      if (selfRequest) setReviseRequest(selfRequest);
      return;
    }
    if (item.actionRequired === "submit_for_signing") {
      if (!hasRole("chief")) {
        setPrompt({ title: tCommon("signing.chiefOnly.title"), text: tCommon("signing.chiefOnly.text"), variant: "error" });
        return;
      }
      const sourceType = item.originalType;
      if (sourceType === "completion_act") {
        setActiveItem(item);
        return;
      }
      if (LEGAL_SOURCE_TYPES.includes(sourceType)) {
        setOrgSignData({
          sourceType,
          entityId: item.entityId!,
          documentType: t(`types.${sourceType}`),
        });
      }
      return;
    }
    if (item.actionRequired === "sign_document") {
      if (isAwaitingSignatureDetails(item.details)) {
        if (!hasRole("chief")) {
          setPrompt({ title: tCommon("signing.chiefOnly.title"), text: tCommon("signing.chiefOnly.text"), variant: "error" });
          return;
        }
        setSigningAmendmentId(item.details.amendment_id);
        return;
      }
      if (!isSignDocumentDetails(item.details)) return;
      const { signer_type, source_type, document_id, candidate_id, sigex_view_url, url, bytes_url } = item.details;
      const viewUrl = sigex_view_url ?? url;

      if (signer_type === "organization" && !hasRole("chief")) {
        setPrompt({ title: tCommon("signing.chiefOnly.title"), text: tCommon("signing.chiefOnly.text"), variant: "error" });
        return;
      }

      if (source_type === "contract_draft" && signer_type === "organization") {
        if (contractSigning.isLoading) return;
        signingContractIdRef.current = item.entityId;
        contractSigning.handleSign();
        return;
      }
      if (source_type === "job_application" && signer_type === "organization" && candidate_id) {
        setJobAppSignData({ candidateId: candidate_id, candidateName: item.workerName, applicationId: item.entityId! });
        return;
      }
      if (source_type === "hiring_order" && signer_type === "organization") {
        setSigningOrderData({ orderId: item.entityId!, candidateName: item.workerName, orderUrl: viewUrl });
        return;
      }

      if (source_type === "business_trip_order" && signer_type === "organization") {
        setAdvanceReview({ id: item.entityId!, type: "trip" });
        return;
      }

      if (
        (source_type === "annual_leave" || source_type === "unpaid_leave" || source_type === "medical_leave") &&
        signer_type === "organization"
      ) {
        const leaveTypeMap: Record<string, LeaveType> = {
          annual_leave: "annual",
          unpaid_leave: "unpaid",
          medical_leave: "medical",
        };
        setSigningLeaveData({ leaveId: item.entityId!, leaveType: leaveTypeMap[source_type] });
        return;
      }

      if (
        (source_type === "amendment_application" ||
          source_type === "amendment_order" ||
          source_type === "amendment_agreement") &&
        signer_type === "organization"
      ) {
        setSigningAmendmentId(extractAmendmentId(item));
        return;
      }

      if (source_type === "resignation_letter" && signer_type === "organization") {
        setSigningResignationId(item.entityId!);
        return;
      }

      if (source_type === "completion_act") {
        setActiveItem(item);
        return;
      }

      if (LEGAL_SOURCE_TYPES.includes(source_type) && signer_type === "organization") {
        setOrgSignData({
          sourceType: source_type,
          entityId: item.entityId!,
          documentType: t(`types.${source_type}`),
        });
        return;
      }

      if (source_type === "salary_advance" && signer_type === "organization") {
        setAdvanceOrgSignData({
          documentId: document_id,
          bytesUrl: bytes_url || undefined,
          workerName: item.workerName,
          documentType: t("types.salary_advance"),
          advanceId: item.details.advance_id,
        });
        return;
      }

      if (signer_type === "counter_signer") {
        setCounterSignData({
          documentId: document_id,
          bytesUrl: bytes_url || undefined,
          fileUrl: item.details.job_application_url || undefined,
          workerName: item.workerName,
          documentType: t(`types.${source_type}`),
          signingStatus: item.details.signing_status,
        });
        return;
      }

      window.open(viewUrl, "_blank");
      return;
    }
    if (item.actionRequired === "acknowledge_order") {
      const orderDetails = isOrderDetails(item.details) ? item.details : null;
      const ackOrderId =
        item.originalType === "business_trip_order"
          ? (orderDetails?.business_trip_id ?? item.entityId)
          : (orderDetails?.leave_id ?? item.entityId);
      setAcknowledgeOrderData({
        orderId: ackOrderId,
        orderType: item.originalType,
        documentUrl: orderDetails?.order_pdf_url ?? null,
        unsignedDocumentUrl: orderDetails?.unsigned_pdf_url ?? null,
        leaveType: orderDetails?.leave_type,
      });
      return;
    }
    if (item.actionRequired === "review_gph_payment") {
      if (isGPHPaymentReviewDetails(item.details)) {
        navigate(`${ACCOUNTING_CONTRACTOR_PAYROLL_DETAIL_PAGE_ROUTE}?year=${item.details.year}&month=${item.details.month}`);
      }
      return;
    }
    if (item.actionRequired === "review_fsm_submission") {
      if (isFSMSubmissionDetails(item.details)) {
        setFsmReviewId(item.details.submission_id);
      }
      return;
    }
    if (item.actionRequired === "approve_data_change" || item.actionRequired === "approve_data_change_chief") {
      setDataChangeReviewData({ requestId: item.entityId!, actionRequired: item.actionRequired });
      return;
    }
    if (item.actionRequired === "approve_project_removal") {
      if (isProjectRemovalRequestDetails(item.details)) {
        setProjectRemovalReview({
          projectId: item.details.project_id,
          requestId: item.details.request_id,
          projectName: item.details.project_name,
          actionType: item.details.action_type,
          reason: item.details.reason,
          initiatedBy: item.details.initiated_by,
        });
      }
      return;
    }
    if (item.actionRequired === "approve_timesheet") {
      const timesheetId = isTimesheetApprovalDetails(item.details) ? item.details.timesheet_id : item.entityId;
      navigate(ACCOUNTING_TIMESHEET_DETAIL_PAGE_ROUTE.replace(":id", String(timesheetId)));
      return;
    }
    if (item.type === "unit_request") {
      setItemRequestId(item.entityId);
      return;
    }
    if (item.type === "advance" || item.type === "business_trip") {
      const details = isAdvanceApprovalDetails(item.details) ? item.details : null;
      const entityId = details?.advance_id ?? details?.business_trip_id ?? (item.entityId as number);
      const entityType = item.type === "business_trip" ? "trip" : "advance";
      setAdvanceReview({ id: entityId, type: entityType });
      return;
    }
    setActiveItem(item);
  }

  function handleModalClose() {
    setActiveItem(null);
    refetch();
  }

  function handleAcknowledgeModalClose() {
    setAcknowledgeOrderData(null);
    refetch();
  }

  async function handleApproveRequest(id: number) {
    if (approvingId !== null) return;
    setApprovingId(id);
    try {
      await approveItemRequest(id).unwrap();
      setItemRequestId(null);
      setPrompt({ title: t("messages.successTitle"), text: t("messages.approveSuccess"), variant: "success" });
      refetch();
    } catch (error) {
      setPrompt({
        title: t("errors.title"),
        text: extractErrorMessage(error, "errors.approveRequest", t),
        variant: "error",
      });
    } finally {
      setApprovingId(null);
    }
  }

  function handleRejectRequest(id: number, reason: string) {
    setConfirmReject({ id, reason });
  }

  async function handleRejectConfirm() {
    if (!confirmReject || rejectingId !== null) return;
    setRejectingId(confirmReject.id);
    try {
      await rejectItemRequest({ id: confirmReject.id, body: { reason: confirmReject.reason } }).unwrap();
      setItemRequestId(null);
      setPrompt({ title: t("messages.successTitle"), text: t("messages.rejectSuccess"), variant: "success" });
      refetch();
    } catch (error) {
      setPrompt({
        title: t("errors.title"),
        text: extractErrorMessage(error, "errors.rejectRequest", t),
        variant: "error",
      });
    } finally {
      setRejectingId(null);
      setConfirmReject(null);
    }
  }

  async function handleCancelRequest(id: number) {
    setConfirmCancel(id);
  }

  async function handleCancelConfirm() {
    if (confirmCancel === null || cancellingId !== null) return;
    setCancellingId(confirmCancel);
    try {
      await cancelItemRequest(confirmCancel).unwrap();
      setItemRequestId(null);
      setPrompt({ title: t("messages.successTitle"), text: t("messages.cancelSuccess"), variant: "success" });
      refetch();
    } catch (error) {
      setPrompt({
        title: t("errors.title"),
        text: extractErrorMessage(error, "errors.cancelRequest", t),
        variant: "error",
      });
    } finally {
      setCancellingId(null);
      setConfirmCancel(null);
    }
  }

  function handleFulfillRequest(id: number) {
    setItemRequestId(null);
    setFulfillRequestId(id);
  }

  const [isFulfilling, setIsFulfilling] = useState(false);

  async function handleFulfillSubmit(id: number, unitIds: number[]) {
    if (isFulfilling) return;
    setIsFulfilling(true);
    try {
      await fulfillItemRequest({ id, body: { unit_ids: unitIds } }).unwrap();
      setFulfillRequestId(null);
      setPrompt({ title: t("messages.successTitle"), text: t("messages.fulfillSuccess"), variant: "success" });
      refetch();
    } catch (error) {
      setPrompt({
        title: t("errors.title"),
        text: extractErrorMessage(error, "errors.fulfillRequest", t),
        variant: "error",
      });
    } finally {
      setIsFulfilling(false);
    }
  }

  function handleItemRequestModalClose() {
    setItemRequestId(null);
    refetch();
  }

  function handleFulfillModalClose() {
    setFulfillRequestId(null);
    refetch();
  }

  async function handlePreviewJobApp(candidateId: number) {
    if (isPreviewingJobApp) return;
    setIsPreviewingJobApp(true);
    try {
      const { blob, filename } = await previewJobApplication(candidateId).unwrap();
      downloadBlob(blob, ensurePdfExtension(filename || `job-application-${candidateId}.pdf`));
    } catch (err) {
      setPrompt({
        title: t("errors.title"),
        text: extractErrorMessage(err, "errors.previewError", t),
        variant: "error",
      });
    } finally {
      setIsPreviewingJobApp(false);
    }
  }

  function extractLeaveIdentifiers(item: NormalizedPendingItem): { leaveId: number; leaveType: LeaveType } {
    let leaveType: LeaveType = "annual";
    let leaveId = item.entityId as number;

    if (isLeaveApprovalDetails(item.details)) {
      leaveType = (item.details.leave_type || "annual") as LeaveType;
      if (item.details.leave_id) {
        leaveId = item.details.leave_id;
      }
    } else if (item.originalType === "medical_order") {
      leaveType = "medical";
    } else if (item.originalType === "vacation_order" && isOrderDetails(item.details)) {
      leaveType = (item.details.leave_type || "annual") as LeaveType;
    } else if (isOrderDetails(item.details) && item.details.leave_type) {
      leaveType = item.details.leave_type as LeaveType;
    }

    return { leaveId, leaveType };
  }

  function extractAmendmentId(item: NormalizedPendingItem): number {
    if (item.originalType === "amendment_order" && isOrderDetails(item.details) && item.details.amendment_id) {
      return item.details.amendment_id;
    }
    return item.entityId as number;
  }

  if (isLoading) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{t("title")}</h1>
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={(val) => {
                if (val) setSortBy(val);
              }}
              width="w-48"
            />
          </div>
          <PendingActionsPageSkeleton />
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
              <h1 className="text-display-xs content-base-primary">{t("title")}</h1>
              <div className="flex items-center gap-4 shrink-0">
                <Select
                  options={sortOptions}
                  value={sortBy}
                  onChange={(val) => {
                    if (val) setSortBy(val);
                  }}
                  width="w-48"
                />
              </div>
            </div>

            {filteredItems.length > 0 ? (
              <div className="flex flex-col gap-3">
                {filteredItems.map((item) => (
                  <PendingActionCard
                    key={`${item.originalType}-${item.id}`}
                    item={item}
                    onClick={() => handleItemClick(item)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                hasActiveFilters={items.length > 0}
                noResultsText={t("emptyFiltered")}
                noDataText={t("empty")}
                noDataHint={t("emptyHint")}
              />
            )}
          </div>

          <PendingActionsFilterPanel
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCategories={selectedCategories}
            onCategoryToggle={handleCategoryToggle}
            selectedPriorities={selectedPriorities}
            onPriorityToggle={handlePriorityToggle}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onReset={handleResetFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
      </section>

      {acknowledgeOrderData && (
        <AcknowledgeOrderModal
          orderId={acknowledgeOrderData.orderId}
          orderType={acknowledgeOrderData.orderType}
          documentUrl={acknowledgeOrderData.documentUrl}
          unsignedDocumentUrl={acknowledgeOrderData.unsignedDocumentUrl}
          leaveType={acknowledgeOrderData.leaveType}
          onClose={handleAcknowledgeModalClose}
        />
      )}

      {activeItem && (
        <>
          {activeItem.type === "leave" &&
            activeItem.entityId !== null &&
            extractLeaveIdentifiers(activeItem).leaveType === "medical" && (
              <MedicalLeaveDetailsModal
                leaveId={extractLeaveIdentifiers(activeItem).leaveId}
                onClose={handleModalClose}
                onSuccess={() => setActiveItem(null)}
              />
            )}

          {activeItem.type === "leave" &&
            activeItem.entityId !== null &&
            extractLeaveIdentifiers(activeItem).leaveType !== "medical" && (
              <LeaveDetailsModal
                leaveId={extractLeaveIdentifiers(activeItem).leaveId}
                leaveType={extractLeaveIdentifiers(activeItem).leaveType}
                onClose={handleModalClose}
                onSignSuccess={() => setActiveItem(null)}
              />
            )}

          {activeItem.type === "amendment" &&
            activeItem.actionRequired !== "approve_draft" &&
            activeItem.entityId !== null && (
              <AmendmentDetailsModal
                amendmentId={extractAmendmentId(activeItem)}
                onClose={handleModalClose}
                onSignSuccess={() => setActiveItem(null)}
              />
            )}

          {activeItem.type === "termination" && activeItem.entityId !== null && (
            <ResignationLetterDetailsModal
              resignationId={activeItem.entityId}
              onClose={handleModalClose}
              onSignSuccess={() => setActiveItem(null)}
            />
          )}

          {activeItem.type === "signing_resignation" && activeItem.entityId !== null && (
            <ResignationLetterDetailsModal
              resignationId={activeItem.entityId}
              onClose={handleModalClose}
              onSignSuccess={() => setActiveItem(null)}
            />
          )}

          {activeItem.type === "hiring" && activeItem.entityId !== null && (
            <OrderDetailsModal
              orderId={activeItem.entityId}
              candidateName={activeItem.workerName}
              orderOnHiring={null}
              originalFileUrlOverride={
                isOrderDetails(activeItem.details)
                  ? (activeItem.details.order_pdf_url ??
                    activeItem.details.document_url ??
                    activeItem.details.order_url ??
                    null)
                  : null
              }
              onClose={handleModalClose}
              onUploaded={handleModalClose}
            />
          )}

          {activeItem.type === "order" &&
            activeItem.originalType === "vacation_order" &&
            activeItem.entityId !== null && (
              <LeaveDetailsModal
                leaveId={extractLeaveIdentifiers(activeItem).leaveId}
                leaveType={extractLeaveIdentifiers(activeItem).leaveType}
                onClose={handleModalClose}
                onSignSuccess={() => setActiveItem(null)}
              />
            )}

          {activeItem.type === "order" &&
            activeItem.originalType === "medical_order" &&
            activeItem.entityId !== null && (
              <LeaveDetailsModal
                leaveId={extractLeaveIdentifiers(activeItem).leaveId}
                leaveType={extractLeaveIdentifiers(activeItem).leaveType}
                onClose={handleModalClose}
                onSignSuccess={() => setActiveItem(null)}
              />
            )}

          {activeItem.type === "order" &&
            activeItem.originalType === "amendment_order" &&
            activeItem.entityId !== null && (
              <AmendmentDetailsModal
                amendmentId={extractAmendmentId(activeItem)}
                onClose={handleModalClose}
                onSignSuccess={() => setActiveItem(null)}
              />
            )}

          {activeItem.actionRequired === "approve_draft" && activeItem.entityId !== null && (
            <AmendmentDetailsModal
              amendmentId={extractAmendmentId(activeItem)}
              onClose={handleModalClose}
              onSignSuccess={() => setActiveItem(null)}
            />
          )}

          {activeItem.type === "signing_act" &&
            activeItem.originalType === "completion_act" &&
            activeItem.entityId !== null && (
              <Suspense fallback={null}>
                <CompletionActSignModal
                  completionActId={activeItem.entityId}
                  counterSignDocumentId={
                    isSignDocumentDetails(activeItem.details) && activeItem.details.signer_type === "counter_signer"
                      ? activeItem.details.document_id
                      : undefined
                  }
                  onClose={handleModalClose}
                  onSuccess={() => {
                    setActiveItem(null);
                    refetch();
                  }}
                />
              </Suspense>
            )}
        </>
      )}

      {itemRequestId !== null && (
        <Suspense fallback={null}>
          <RequestDetailModal
            requestId={itemRequestId}
            onClose={handleItemRequestModalClose}
            onApprove={handleApproveRequest}
            onReject={handleRejectRequest}
            onCancel={handleCancelRequest}
            onFulfill={handleFulfillRequest}
            approvingId={approvingId}
            cancellingId={cancellingId}
            currentUserId={user?.id}
          />
        </Suspense>
      )}

      {fulfillRequestId !== null && (
        <Suspense fallback={null}>
          <FulfillRequestModal
            requestId={fulfillRequestId}
            onClose={handleFulfillModalClose}
            onConfirm={handleFulfillSubmit}
            isLoading={isFulfilling}
          />
        </Suspense>
      )}

      {confirmReject !== null && (
        <PromptForm
          title={t("confirm.rejectTitle")}
          text={t("confirm.rejectText")}
          variant="error"
          onClose={() => setConfirmReject(null)}
          onConfirm={handleRejectConfirm}
          isLoading={rejectingId !== null}
          confirmText={t("confirm.rejectConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="PendingActionsPage"
        />
      )}

      {confirmCancel !== null && (
        <PromptForm
          title={t("confirm.cancelTitle")}
          text={t("confirm.cancelText")}
          variant="warning"
          onClose={() => setConfirmCancel(null)}
          onConfirm={handleCancelConfirm}
          isLoading={cancellingId !== null}
          confirmText={t("confirm.cancelConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="PendingActionsPage"
        />
      )}

      {contractSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${contractSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {counterSignData && (
        <CounterSignDocumentModal
          documentId={counterSignData.documentId}
          bytesUrl={counterSignData.bytesUrl}
          fileUrl={counterSignData.fileUrl}
          workerName={counterSignData.workerName}
          documentType={counterSignData.documentType}
          signingStatus={counterSignData.signingStatus}
          onClose={() => {
            setCounterSignData(null);
            refetch();
          }}
          onSuccess={() => {
            setCounterSignData(null);
          }}
        />
      )}

      {jobAppSignData && (
        <JobApplicationDetailsModal
          candidateName={jobAppSignData.candidateName}
          candidateId={jobAppSignData.candidateId}
          applicationId={jobAppSignData.applicationId}
          onClose={() => {
            setJobAppSignData(null);
            refetch();
          }}
          onSignSuccess={() => {
            setJobAppSignData(null);
            refetch();
          }}
          onPreview={() => handlePreviewJobApp(jobAppSignData.candidateId)}
          isPreviewing={isPreviewingJobApp}
        />
      )}

      {signingOrderData && (
        <OrderDetailsModal
          orderId={signingOrderData.orderId}
          candidateName={signingOrderData.candidateName}
          orderOnHiring={null}
          originalFileUrlOverride={signingOrderData.orderUrl}
          onClose={() => {
            setSigningOrderData(null);
            refetch();
          }}
          onUploaded={() => {
            setSigningOrderData(null);
            refetch();
          }}
        />
      )}

      {signingLeaveData && (
        <LeaveDetailsModal
          leaveId={signingLeaveData.leaveId}
          leaveType={signingLeaveData.leaveType}
          onClose={() => {
            setSigningLeaveData(null);
            refetch();
          }}
          onSignSuccess={() => setSigningLeaveData(null)}
        />
      )}

      {signingAmendmentId !== null && (
        <AmendmentDetailsModal
          amendmentId={signingAmendmentId}
          onClose={() => {
            setSigningAmendmentId(null);
            refetch();
          }}
          onSignSuccess={() => setSigningAmendmentId(null)}
        />
      )}

      {signingResignationId !== null && (
        <ResignationLetterDetailsModal
          resignationId={signingResignationId}
          onClose={() => {
            setSigningResignationId(null);
            refetch();
          }}
          onSignSuccess={() => setSigningResignationId(null)}
        />
      )}

      {orgSignData && (
        <Suspense fallback={null}>
          <OrgSignDocumentModal
            sourceType={orgSignData.sourceType}
            entityId={orgSignData.entityId}
            documentType={orgSignData.documentType}
            onClose={() => {
              setOrgSignData(null);
              refetch();
            }}
            onSuccess={() => {
              setOrgSignData(null);
              refetch();
            }}
          />
        </Suspense>
      )}

      {showSignPersonalSheet && (
        <Suspense fallback={null}>
          <SignPersonalSheetModal
            onClose={() => {
              setShowSignPersonalSheet(false);
              refetch();
            }}
            onSignSuccess={() => {
              setShowSignPersonalSheet(false);
              refetch();
            }}
          />
        </Suspense>
      )}

      {advanceOrgSignData && (
        <Suspense fallback={null}>
          <OrgSignByDocumentModal
            documentId={advanceOrgSignData.documentId}
            bytesUrl={advanceOrgSignData.bytesUrl}
            workerName={advanceOrgSignData.workerName}
            documentType={advanceOrgSignData.documentType}
            advanceId={advanceOrgSignData.advanceId}
            onClose={() => {
              setAdvanceOrgSignData(null);
              refetch();
            }}
            onSuccess={() => {
              setAdvanceOrgSignData(null);
              refetch();
            }}
          />
        </Suspense>
      )}

      {reviseRequest && (
        <Suspense fallback={null}>
          {(reviseRequest.type === "annual_leave" ||
            reviseRequest.type === "unpaid_leave" ||
            reviseRequest.type === "medical_leave") && (
            <EditSelfLeaveForm
              request={reviseRequest}
              onClose={() => {
                setReviseRequest(null);
                refetch();
              }}
              onSuccess={() => {
                setReviseRequest(null);
                refetch();
              }}
            />
          )}
          {reviseRequest.type === "amendment" && (
            <EditSelfAmendmentForm
              request={reviseRequest}
              onClose={() => {
                setReviseRequest(null);
                refetch();
              }}
              onSuccess={() => {
                setReviseRequest(null);
                refetch();
              }}
            />
          )}
          {reviseRequest.type === "resignation" && (
            <EditSelfResignationForm
              request={reviseRequest}
              onClose={() => {
                setReviseRequest(null);
                refetch();
              }}
              onSuccess={() => {
                setReviseRequest(null);
                refetch();
              }}
            />
          )}
        </Suspense>
      )}

      {dataChangeReviewData && (
        <Suspense fallback={null}>
          <DataChangeApprovalModal
            requestId={dataChangeReviewData.requestId}
            actionRequired={dataChangeReviewData.actionRequired}
            onClose={() => {
              setDataChangeReviewData(null);
              refetch();
            }}
            onSuccess={(message) => {
              setDataChangeReviewData(null);
              setPrompt({ title: t("messages.successTitle"), text: message, variant: "success" });
              refetch();
            }}
            onError={(message) => {
              setPrompt({ title: t("errors.title"), text: message, variant: "error" });
            }}
          />
        </Suspense>
      )}

      {projectRemovalReview && (
        <Suspense fallback={null}>
          <ProjectRemovalApprovalModal
            projectId={projectRemovalReview.projectId}
            requestId={projectRemovalReview.requestId}
            projectName={projectRemovalReview.projectName}
            actionType={projectRemovalReview.actionType}
            reason={projectRemovalReview.reason}
            initiatedBy={projectRemovalReview.initiatedBy}
            onClose={() => {
              setProjectRemovalReview(null);
              refetch();
            }}
            onSuccess={(message) => {
              setProjectRemovalReview(null);
              setPrompt({ title: t("messages.successTitle"), text: message, variant: "success" });
              refetch();
            }}
            onError={(message) => {
              setPrompt({ title: t("errors.title"), text: message, variant: "error" });
            }}
          />
        </Suspense>
      )}

      {advanceReview !== null && (
        <Suspense fallback={null}>
          <AdvanceDetailsModal
            entityId={advanceReview.id}
            entityType={advanceReview.type}
            onClose={() => {
              setAdvanceReview(null);
              refetch();
            }}
            onActionSuccess={() => {
              setAdvanceReview(null);
              refetch();
            }}
          />
        </Suspense>
      )}

      {fsmReviewId && (
        <Suspense fallback={null}>
          <FSMReviewModal
            submissionId={fsmReviewId}
            onClose={() => {
              setFsmReviewId(null);
              refetch();
            }}
            onSuccess={(message) => {
              setFsmReviewId(null);
              setPrompt({ title: t("messages.successTitle"), text: message, variant: "success" });
              refetch();
            }}
            onError={(message) => {
              setPrompt({ title: t("errors.title"), text: message, variant: "error" });
            }}
          />
        </Suspense>
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="PendingActionsPage"
        />
      )}

      {waitItem && (
        <Prompt
          title={t("tabs.wait")}
          text={t(`wait.${waitItem.originalType}`)}
          variant="warning"
          onClose={() => setWaitItem(null)}
          buttonText={t("prompt.ok")}
          namespace="PendingActionsPage"
        />
      )}
    </>
  );
}
